// =============================================================================
// What a screen shows while it waits.
//
// The word "Loading…" on its own is indistinguishable from a frozen app, which
// is exactly how it was read at the desk. A moving spinner says the app is
// alive; the stall bar (below, mounted once by the shell) says it is not, and
// gives staff something to press instead of the back of a chair.
// =============================================================================

import { el } from './dom.js';
import { T } from './strings.js';
import { onStall } from './activity.js';

/** The standard waiting state. Use everywhere a screen used to print T.loading. */
export function spinner(label = T.loading) {
  return el('div', { class: 'loading', role: 'status', 'aria-live': 'polite' },
    el('span', { class: 'spinner', 'aria-hidden': 'true' }),
    el('span', {}, label),
  );
}

/**
 * The stuck-request bar. Mounted once by the shell, sits out of the way in the
 * bottom corner, and appears only when a call has been in flight long enough to
 * count as stuck. `onRetry` re-runs whatever the app was doing.
 */
export function mountStallBar(onRetry) {
  const bar = el('div', { class: 'stall-bar hidden', role: 'status' },
    el('span', {}, T.stillWorking),
    el('button', { class: 'btn btn-sm', onClick: onRetry }, T.retry),
  );
  document.body.append(bar);
  onStall((stalled) => bar.classList.toggle('hidden', !stalled));
  return bar;
}

/**
 * Placeholder rows in the shape of the real ones.
 *
 * A centred spinner replacing the whole table means the page collapses to
 * nothing and then jumps back to full height — on a slow connection that
 * happens on every search. A skeleton holds the space, so the layout never
 * moves and the wait reads as "this is filling in" rather than "this is empty".
 *
 * aria-hidden: it is decoration. The surrounding region announces the wait.
 */
export function skeletonRows(rows = 6, cols = 4) {
  return el('div', { class: 'skeleton-table', 'aria-hidden': 'true' },
    ...Array.from({ length: rows }, () =>
      el('div', { class: 'skeleton-row' },
        ...Array.from({ length: cols }, (_, i) =>
          el('span', { class: `skeleton-cell skeleton-cell-${i === 0 ? 'wide' : 'narrow'}` })))));
}

/** The same idea for a stack of cards or a side panel. */
export function skeletonBlock(lines = 3) {
  return el('div', { class: 'skeleton-block', 'aria-hidden': 'true' },
    ...Array.from({ length: lines }, (_, i) =>
      el('span', { class: `skeleton-line ${i === lines - 1 ? 'skeleton-line-short' : ''}` })));
}
